// Google Cloud Pub/Sub Service for vaiu-app
interface PubSubEvent {
  eventType: string;
  timestamp: string;
  data: any;
  metadata: {
    source: string;
    version: string;
    userId?: string;
    sessionId?: string;
  };
}

interface PubSubConfig {
  enabled: boolean;
  apiEndpoint: string;
  topics: {
    userEvents: string;
    notificationEvents: string;
    uiEvents: string;
    authEvents: string;
    performanceEvents: string;
  };
}

class PubSubService {
  private config: PubSubConfig;
  private isEnabled: boolean = false;
  private sessionId: string;

  constructor() {
    this.sessionId = this.generateSessionId();
    this.config = {
      enabled: false,
      apiEndpoint: '/api/v1/pubsub/publish',
      topics: {
        userEvents: 'user-events',
        notificationEvents: 'notification-events', 
        uiEvents: 'ui-events',
        authEvents: 'auth-events',
        performanceEvents: 'performance-events'
      }
    };
    this.init();
  }

  private generateSessionId(): string {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  private async init(): Promise<void> {
    try {
      // Check if Pub/Sub is enabled via backend health endpoint
      // Correct endpoint: /api/v1/pubsub/health (not /status)
      const response = await fetch('/api/v1/pubsub/health');
      if (response.ok) {
        // Check if response is JSON
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
          const health = await response.json();
          // Health endpoint returns: { status: "healthy", enabled: true, projectId: "...", topics: [...] }
          this.isEnabled = health.enabled === true && health.status === 'healthy';
          if (import.meta.env.DEV) {
            console.log('[PubSub] Service initialized:', { enabled: this.isEnabled, health });
          }
        } else {
          // Non-JSON response means endpoint doesn't exist
          this.isEnabled = false;
        }
      } else if (response.status === 404) {
        // 404 - endpoint doesn't exist, silently disable service
        if (import.meta.env.DEV) {
          console.debug('[PubSub] Health endpoint not available (404), disabling service');
        }
        this.isEnabled = false;
      } else {
        // Other error - endpoint exists but returned error
        if (import.meta.env.DEV) {
          console.debug('[PubSub] Health check failed:', response.status, response.statusText);
        }
        this.isEnabled = false;
      }
    } catch (error: any) {
      // Silently handle errors - endpoint may not be implemented yet
      // Only log in development mode
      if (import.meta.env.DEV) {
        console.debug('[PubSub] Service not available:', error?.message);
      }
      this.isEnabled = false;
    }
  }

  private async publishEvent(topicName: string, eventType: string, data: any, metadata: any = {}): Promise<string | null> {
    if (!this.isEnabled) {
      console.debug('[PubSub] Service disabled, skipping event publication');
      return null;
    }

    try {
      const authToken = localStorage.getItem('authToken');
      const userId = this.getCurrentUserId();

      const eventData: PubSubEvent = {
        eventType,
        timestamp: new Date().toISOString(),
        data,
        metadata: {
          ...metadata,
          source: 'vaiu-app',
          version: '1.0',
          userId,
          sessionId: this.sessionId
        }
      };

      const response = await fetch(this.config.apiEndpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': authToken ? `Bearer ${authToken}` : ''
        },
        body: JSON.stringify({
          topic: topicName,
          event: eventData
        })
      });

      if (response.ok) {
        const result = await response.json();
        console.debug(`[PubSub] Event published to ${topicName}:`, { eventType, messageId: result.messageId });
        return result.messageId;
      } else {
        console.warn(`[PubSub] Failed to publish event to ${topicName}:`, response.status);
        return null;
      }
    } catch (error) {
      console.error(`[PubSub] Error publishing event to ${topicName}:`, error);
      return null;
    }
  }

  private getCurrentUserId(): string | undefined {
    try {
      const authToken = localStorage.getItem('authToken');
      if (authToken) {
        const payload = JSON.parse(atob(authToken.split('.')[1]));
        return payload.userId || payload.sub;
      }
    } catch (error) {
      // Ignore errors when getting user ID
    }
    return undefined;
  }

  // User Events
  async publishUserLogin(loginMethod: string, success: boolean): Promise<string | null> {
    return this.publishEvent(this.config.topics.authEvents, 'USER_LOGIN_ATTEMPT', {
      loginMethod,
      success,
      userAgent: navigator.userAgent,
      timestamp: new Date().toISOString()
    });
  }

  async publishUserLogout(): Promise<string | null> {
    return this.publishEvent(this.config.topics.authEvents, 'USER_LOGOUT', {
      sessionDuration: this.getSessionDuration(),
      timestamp: new Date().toISOString()
    });
  }

  async publishPageView(pagePath: string, pageTitle?: string): Promise<string | null> {
    return this.publishEvent(this.config.topics.uiEvents, 'PAGE_VIEW', {
      path: pagePath,
      title: pageTitle,
      referrer: document.referrer,
      userAgent: navigator.userAgent
    });
  }

  // Notification Events
  async publishNotificationPermissionRequest(granted: boolean): Promise<string | null> {
    return this.publishEvent(this.config.topics.notificationEvents, 'NOTIFICATION_PERMISSION_REQUESTED', {
      granted,
      permission: Notification.permission,
      userAgent: navigator.userAgent
    });
  }

  async publishNotificationSubscription(method: 'vapid' | 'fcm', success: boolean, error?: string): Promise<string | null> {
    return this.publishEvent(this.config.topics.notificationEvents, 'NOTIFICATION_SUBSCRIPTION', {
      method,
      success,
      error,
      supportsPush: 'PushManager' in window,
      hasServiceWorker: 'serviceWorker' in navigator
    });
  }

  async publishNotificationReceived(notificationType: string, foreground: boolean): Promise<string | null> {
    return this.publishEvent(this.config.topics.notificationEvents, 'NOTIFICATION_RECEIVED', {
      notificationType,
      foreground,
      permission: Notification.permission,
      documentVisible: !document.hidden
    });
  }

  async publishNotificationClicked(notificationData: any): Promise<string | null> {
    return this.publishEvent(this.config.topics.notificationEvents, 'NOTIFICATION_CLICKED', {
      notificationData,
      timestamp: new Date().toISOString()
    });
  }

  // Transaction Events (from UI perspective)
  async publishTransactionInitiated(transactionType: string, amount?: number, currency?: string): Promise<string | null> {
    return this.publishEvent(this.config.topics.userEvents, 'TRANSACTION_INITIATED', {
      transactionType,
      amount,
      currency,
      userAgent: navigator.userAgent
    });
  }

  async publishTransactionCompleted(transactionType: string, success: boolean, error?: string): Promise<string | null> {
    return this.publishEvent(this.config.topics.userEvents, 'TRANSACTION_COMPLETED', {
      transactionType,
      success,
      error
    });
  }

  // Performance Events
  async publishPerformanceMetrics(metrics: any): Promise<string | null> {
    return this.publishEvent(this.config.topics.performanceEvents, 'PERFORMANCE_METRICS', {
      ...metrics,
      userAgent: navigator.userAgent,
      connection: (navigator as any).connection ? {
        effectiveType: (navigator as any).connection.effectiveType,
        downlink: (navigator as any).connection.downlink
      } : null
    });
  }

  async publishError(error: Error, context?: any): Promise<string | null> {
    return this.publishEvent(this.config.topics.uiEvents, 'CLIENT_ERROR', {
      message: error.message,
      stack: error.stack,
      context,
      userAgent: navigator.userAgent,
      url: window.location.href
    });
  }

  // Feature Usage Events
  async publishFeatureUsage(featureName: string, action: string, metadata?: any): Promise<string | null> {
    return this.publishEvent(this.config.topics.uiEvents, 'FEATURE_USAGE', {
      featureName,
      action,
      metadata,
      timestamp: new Date().toISOString()
    });
  }

  // Utility methods
  private getSessionDuration(): number {
    const sessionStart = parseInt(this.sessionId.split('_')[1]);
    return Date.now() - sessionStart;
  }

  async getStatus(): Promise<{ enabled: boolean; topics: string[]; sessionId: string }> {
    return {
      enabled: this.isEnabled,
      topics: Object.values(this.config.topics),
      sessionId: this.sessionId
    };
  }

  // Enable/disable the service
  setEnabled(enabled: boolean): void {
    this.isEnabled = enabled;
    console.log(`[PubSub] Service ${enabled ? 'enabled' : 'disabled'}`);
  }
}

// Create singleton instance
const pubSubService = new PubSubService();

export default pubSubService; 